import { apiClient } from "../utils/kiota-client";
import type { ProductAdministrationDto } from "../../../RoyalFloraHolland.Aap.Services.Api.Web.Kiota/Typescript/Odata/V1/models/index";

export interface ProductListResult {
  value: ProductAdministrationDto[];
  "@odata.count"?: number;
  error?: {
    message: string;
    statusCode?: number;
  };
}

export interface ProductFilters {
  page?: number;
  pageSize?: number;
  expired?: boolean;
  hasPrimaryCharacteristic?: boolean;
  productCode?: string;
  productName?: string;
  productGroupCode?: string;
  ingangsdatum?: string;
  gewasnummer?: string;
}

/**
 * Loads products from the API using the Kiota client
 */
export async function loadProducts(
  filters: ProductFilters = {},
): Promise<ProductListResult> {
  try {
    const {
      page = 1,
      pageSize = 100,
      expired,
      hasPrimaryCharacteristic,
      productCode,
      productName,
      productGroupCode,
      ingangsdatum,
      gewasnummer,
    } = filters;

    const filterConditions: string[] = [];

    if (expired !== undefined) {
      filterConditions.push(`expired eq ${expired}`);
    }

    if (hasPrimaryCharacteristic !== undefined) {
      filterConditions.push(
        `hasPrimaryCharacteristic eq ${hasPrimaryCharacteristic}`,
      );
    }

    if (productCode) {
      filterConditions.push(`productCode eq ${productCode}`);
    }

    if (productName) {
      const searchLower = productName.toLowerCase().replace(/'/g, "''");
      filterConditions.push(`contains(tolower(productName), '${searchLower}')`);
    }

    if (productGroupCode) {
      filterConditions.push(`productGroupCode eq ${productGroupCode}`);
    }

    if (ingangsdatum) {
      filterConditions.push(`productStartDate ge ${ingangsdatum}`);
    }

    if (gewasnummer) {
      // Sanitize gewasnummer to ensure only the taxonomicNumber is used (strip label like " - ID: ...")
      const leadingDigitsMatch = gewasnummer.trim().match(/^(\d+)/);
      const sanitizedGewasnummer = leadingDigitsMatch?.[0];
      if (sanitizedGewasnummer) {
        filterConditions.push(`taxonomicNumber eq ${sanitizedGewasnummer}`);
      }
    }

    const filterString =
      filterConditions.length > 0 ? filterConditions.join(" and ") : undefined;

    const queryConfig = {
      queryParameters: {
        count: true,
        top: pageSize,
        skip: (page - 1) * pageSize,
        ...(filterString && {
          filter: filterString,
        }),
      },
    };

    const response =
      await apiClient.productAdministrationOverview.get(queryConfig);

    if (!response) {
      return {
        value: [],
        "@odata.count": 0,
      };
    }

    // Kiota stores @odata properties in additionalData
    const count = (response as any).additionalData?.["@odata.count"];
    const valueLength = response.value?.length || 0;

    // If API doesn't return count, estimate based on page size
    const estimatedCount =
      count !== undefined
        ? count
        : valueLength === pageSize
          ? (page + 1) * pageSize
          : page * pageSize - (pageSize - valueLength);

    return {
      value: response.value || [],
      "@odata.count": estimatedCount,
    };
  } catch (error) {
    console.error("Failed to load products:", error);

    const statusCode =
      (error as any)?.statusCode || (error as any)?.response?.status;

    return {
      value: [],
      "@odata.count": 0,
      error: {
        message:
          error instanceof Error ? error.message : "Failed to load products",
        statusCode,
      },
    };
  }
}

/**
 * Loads a single product by its code
 */
export async function loadProductByCode(
  productCode: number,
): Promise<ProductAdministrationDto | null> {
  try {
    const response = await apiClient.productAdministrationOverview
      .byProductCode(productCode)
      .get();

    return response || null;
  } catch (error) {
    console.error(`Failed to load product ${productCode}:`, error);
    return null;
  }
}
